/* eslint-disable react/jsx-props-no-spreading */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { components, MenuListProps, MultiValueProps, OptionProps } from 'react-select';
import Auth from 'modules/Auth';
import usePendoTracking from 'customHooks/usePendoTracking';
import RequiredAsterisk from 'components/common/RequiredAsterisk';
import { CreativeMarketDeal, CreativeStatus } from 'components/common/types/Creative.types';
import MultipleSwitchBox from 'lib/MultipleSwitchBox';
import { MotionType, MOTION_TYPE_INFO } from 'consts/motionType';
import { PermissionsEnum } from 'consts/permissions';
import AsyncSelect from 'lib/AsyncSelect';
import { AsyncSelectLoadDataHandler, SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { getIsReseller } from 'store/publisher/selectors';
import { Store } from 'components/common/types/Store.types';
import { getDealsByOrigin } from 'store/creative/selectors';
import Card from 'lib/Card';
import { CardTheme } from 'lib/Card/Card.types';
import { CircularLoadingSpinner, SpinnerSize } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import DataList from 'lib/DataList';
import DataListProps from 'lib/DataList/DataList.types';
import Chip, { Color, Width } from 'lib/Chip';

type PreviewDealProps = {
  defaultDealOptions: DataListProps[];
  isLoadingDealOptions: boolean;
  filterLoadedLookupData: AsyncSelectLoadDataHandler<DataListProps>;
  onSelectDealId: (selectedValue: CreativeMarketDeal[]) => Promise<void>;
  onMotionChange: (motionValue: MotionType) => Promise<void>;
};

const PreviewDeal = ({
  defaultDealOptions,
  isLoadingDealOptions,
  filterLoadedLookupData,
  onSelectDealId,
  onMotionChange,
}: PreviewDealProps): React.ReactElement => {
  const { getPendoDataId } = usePendoTracking();
  const isReseller = useSelector(getIsReseller);
  const { ownDealIds, inheritedDealIds } = useSelector(getDealsByOrigin);
  const { statusCode, deleted, movement } = useSelector((state: Store) => state.creative);

  const ownDealIdsAsDealLookupData = useMemo(() => {
    return ownDealIds.map((deal) => {
      const option = new Map(defaultDealOptions.map((dealOption) => [dealOption.subText, dealOption])).get(deal.dealId);
      return {
        id: deal.id,
        title: option?.title ?? deal.dealName ?? deal.dealId,
        subText: deal.dealId,
        status: option?.status,
      };
    });
  }, [defaultDealOptions, ownDealIds]);

  const noOptionsMessage = useMemo(() => {
    if (isLoadingDealOptions) return () => 'Loading...';
    if (!defaultDealOptions.length) return () => 'Something went wrong';

    return undefined;
  }, [isLoadingDealOptions]);

  const onSelect: SelectHandler<DataListProps> = (_, value: DataListProps[]) => {
    onSelectDealId(
      value.map((item) => ({
        id: item.id,
        dealId: item.subText,
        dealName: item.title,
      })),
    );
  };

  return (
    <Card theme={CardTheme.TRANSPARENT_WITH_BORDER}>
      <div className="p-4 flex flex-wrap justify-between">
        <div className="inline-flex flex-col gap-2.5 w-3/4">
          <span className="sub-header-base text-neutral-600">
            Campaign ID/name or Deal line ID/name {isReseller && <RequiredAsterisk />}
          </span>
          <div className="relative async-select-indicators-top">
            <AsyncSelect
              name="campaignId"
              loadData={filterLoadedLookupData}
              defaultOptions={defaultDealOptions}
              onSelect={onSelect}
              placeholder="Search by name or ID..."
              selectedValue={ownDealIdsAsDealLookupData}
              multiple
              closeMenuOnSelect={false}
              dataTestId="creative-campaign-search"
              dataPendoId={getPendoDataId('Search Deal ID')}
              isDisabled={
                statusCode === CreativeStatus.REJECTED ||
                Boolean(deleted) ||
                (isReseller && statusCode === CreativeStatus.SENT_FOR_APPROVAL)
              }
              textKey="title"
              valueKey="subText"
              noOptionsMessage={noOptionsMessage}
              customComponents={{
                MultiValue: ({
                  data,
                  removeProps,
                  selectProps,
                }: MultiValueProps & { data: DataListProps }): React.ReactElement => (
                  <div className="mb-1 flex w-full items-center gap-1 rounded-md border border-neutral-950-opacity-20 bg-white py-1 pl-2 pr-1 shadow-inner-sm">
                    <div className="min-w-0 flex-1">
                      <DataList data={data} variant="chip" isLoadingDealOptions={isLoadingDealOptions} />
                    </div>
                    <components.MultiValueRemove data={data} innerProps={removeProps} selectProps={selectProps} />
                  </div>
                ),
                Option: ({ data, ...props }: OptionProps & { data: DataListProps }): React.ReactElement => (
                  <components.Option data={data} {...props}>
                    <DataList data={data} variant="option" />
                  </components.Option>
                ),
                MenuList: ({ children, ...props }: MenuListProps): React.ReactElement => (
                  <components.MenuList {...props}>
                    {children}
                    {isLoadingDealOptions && (
                      <div className="flex justify-center py-6">
                        <CircularLoadingSpinner size={SpinnerSize.SMALL} />
                      </div>
                    )}
                  </components.MenuList>
                ),
              }}
            />
          </div>
          <div
            className="mt-2 min-h-[36px] flex flex-wrap gap-x-7 gap-y-1"
            data-test-id="creative-inherited-campaign-ids"
          >
            {inheritedDealIds?.length > 0 &&
              inheritedDealIds.map((deal) => (
                <Chip
                  key={deal.id}
                  color={Color.PRIMARY}
                  isDisabled
                  label={deal.dealId}
                  dataTestId={`creative-inherited-chip-${deal.dealId}`}
                  truncateLabel={false}
                  width={Width.FULL}
                />
              ))}
          </div>
        </div>
        {Auth.hasPermission(PermissionsEnum.CONTENT_MOTION_TOGGLE) && (
          <div className="flex items-start">
            <MultipleSwitchBox
              source={[MOTION_TYPE_INFO.STILL, MOTION_TYPE_INFO.MOVING]}
              selectedValue={movement}
              textKey="name"
              valueKey="code"
              onChange={(value: MotionType) => onMotionChange(value)}
              isDisabled={Boolean(deleted)}
              dataTestId="creative-motion-switch"
            />
          </div>
        )}
      </div>
    </Card>
  );
};

export default PreviewDeal;
/* eslint-enable react/jsx-props-no-spreading */
